/**
 * The dropdown.
 *
 * Native select popups are drawn by the OS: they ignore the theme, cannot show
 * an icon or a colour, and behave badly inside a frameless window. This is the
 * replacement - themed, animated, keyboard driven, and searchable once the list
 * is long enough to need it.
 */

import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/cn';
import { Icon } from '@/icons/Icon';

export type Option = {
  value: string;
  label: string;
  sub?: string;
  /** Icon name from the set, drawn on the left of the row. */
  icon?: string;
  /** Tints the icon - used by games, themes and anything with a brand colour. */
  color?: string;
  /** A real image instead of a drawn glyph - game and badge artwork. */
  art?: string;
  group?: string;
};

type Props = {
  value: string;
  options: Option[];
  onChange: (value: string) => void;
  placeholder?: string;
  /** true | false | 'auto' (auto shows the box past nine options). */
  search?: boolean | 'auto';
  size?: 'sm' | 'md';
  /** Shrink to content instead of filling the row. */
  auto?: boolean;
  /** Icon on the trigger itself, before the current label. */
  lead?: string;
  disabled?: boolean;
  className?: string;
  /**
   * What the dropdown chooses ("Genre", "Region"). Announced together with the
   * current value, so a screen reader hears "Genre, All genres" rather than a
   * bare "All genres" button with no idea what it changes.
   */
  'aria-label'?: string;
};

export function Select({
  value, options, onChange, placeholder = 'Select…',
  search = 'auto', size = 'md', auto, lead, disabled, className,
  'aria-label': label
}: Props) {
  const listId = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [cursor, setCursor] = useState(0);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const [box, setBox] = useState<DOMRect | null>(null);

  const current = options.find((o) => o.value === value);
  const showSearch = search === 'auto' ? options.length > 9 : !!search;

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => (o.label + ' ' + (o.sub ?? '')).toLowerCase().includes(q));
  }, [options, query]);

  // Grouped, but only when the options actually say they belong to groups.
  const grouped = useMemo(() => {
    const out: { group: string | null; items: Option[] }[] = [];
    for (const o of shown) {
      const g = o.group ?? null;
      const last = out[out.length - 1];
      if (last && last.group === g) last.items.push(o);
      else out.push({ group: g, items: [o] });
    }
    return out;
  }, [shown]);

  const close = useCallback(() => {
    setOpen(false);
    setQuery('');
  }, []);

  // Measured on open so the panel lands under the trigger at its width.
  useLayoutEffect(() => {
    if (!open) return;
    const measure = () => setBox(triggerRef.current?.getBoundingClientRect() ?? null);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    setCursor(Math.max(0, shown.findIndex((o) => o.value === value)));
  }, [open]);

  useEffect(() => { setCursor(0); }, [query]);

  useEffect(() => {
    if (!open) return;
    listRef.current?.querySelector('[data-active="1"]')?.scrollIntoView({ block: 'nearest' });
  }, [cursor, open]);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      const t = e.target as Node;
      if (listRef.current?.contains(t) || triggerRef.current?.contains(t)) return;
      close();
    }
    const t = setTimeout(() => document.addEventListener('mousedown', onDown, true), 0);
    return () => { clearTimeout(t); document.removeEventListener('mousedown', onDown, true); };
  }, [open, close]);

  function pick(option: Option) {
    close();
    if (option.value !== value) onChange(option.value);
    triggerRef.current?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ' || e.key === 'ArrowDown') {
        e.preventDefault();
        setOpen(true);
      }
      return;
    }
    if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); setCursor((c) => (shown.length ? (c + 1) % shown.length : 0)); return; }
    if (e.key === 'ArrowUp') { e.preventDefault(); setCursor((c) => (shown.length ? (c - 1 + shown.length) % shown.length : 0)); return; }
    if (e.key === 'Enter') {
      e.preventDefault();
      const option = shown[cursor];
      if (option) pick(option);
    }
  }

  /* The shared control heights, so a dropdown lines up with the search box
     and buttons beside it. */
  const height = size === 'sm' ? 'h-[var(--h-sm)]' : 'h-[var(--h-md)]';
  const activeId = open && shown[cursor] ? listId + '-' + cursor : undefined;

  // Below the trigger normally; above when there is more room up there.
  const panelTop = box ? box.bottom + 6 : 0;
  const flip = box ? panelTop + 280 > window.innerHeight && box.top > 300 : false;

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-activedescendant={showSearch ? undefined : activeId}
        aria-label={label ? label + ', ' + (current?.label ?? placeholder) : undefined}
        onClick={() => setOpen(!open)}
        onKeyDown={onKeyDown}
        className={cn(
          'sel-trigger group flex items-center gap-2 rounded-[calc(12px*var(--radius-mul,1))] border bg-surface-1 px-3.5 text-left',
          'transition-[border-color,background,box-shadow] duration-150',
          'disabled:pointer-events-none disabled:opacity-45',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent-hi',
          height,
          auto ? 'w-auto' : 'w-full',
          size === 'sm' ? 'text-[12.5px]' : 'text-[13px]',
          open
            ? 'border-accent bg-surface-2 shadow-[0_0_0_3px_color-mix(in_srgb,var(--accent)_14%,transparent)]'
            : 'border-line-soft hover:border-line hover:bg-surface-2',
          className
        )}
      >
        {lead ? <Icon name={lead} size={16} className="flex-none text-mute" /> : null}
        {current?.art ? (
          <img src={current.art} alt="" className="h-[18px] w-[18px] flex-none rounded-[calc(6px*var(--radius-mul,1))] object-cover" />
        ) : current?.icon ? (
          <Icon name={current.icon} size={17} className="flex-none" style={{ color: current.color }} />
        ) : null}
        <span className={cn('min-w-0 flex-1 truncate font-semibold', !current && 'text-mute')}>
          {current?.label ?? placeholder}
        </span>
        <Icon
          name="chevronD"
          size={15}
          className={cn('flex-none text-mute transition-transform duration-200', open && 'rotate-180')}
        />
      </button>

      {open && box
        ? createPortal(
            <div
              ref={listRef}
              data-keeps-card
              style={{
                left: box.left,
                width: Math.max(box.width, 190),
                ...(flip
                  ? { bottom: window.innerHeight - box.top + 6 }
                  : { top: panelTop })
              }}
              className={cn(
                'fixed z-[400] flex max-h-[300px] flex-col overflow-hidden rounded-[var(--radius)] border border-line bg-surface-3 shadow-[var(--shadow-3)]',
                flip ? 'origin-bottom animate-[selectup_150ms_var(--ease-out-soft)]' : 'origin-top animate-[selectdown_150ms_var(--ease-out-soft)]'
              )}
            >
              {showSearch ? (
                <div className="flex-none border-b border-line-soft p-1.5">
                  <input
                    autoFocus
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    onKeyDown={onKeyDown}
                    aria-label={label ? 'Filter ' + label.toLowerCase() + ' options' : 'Filter options'}
                    aria-controls={listId}
                    aria-activedescendant={activeId}
                    placeholder="Search…"
                    spellCheck={false}
                    className="h-8 w-full rounded-[var(--radius-s)] border border-line bg-surface-1 px-2.5 text-[12.5px] outline-none focus:border-accent"
                  />
                </div>
              ) : null}

              <div
                id={listId}
                role="listbox"
                aria-label={label ?? placeholder}
                className="min-h-0 flex-1 overflow-y-auto p-1"
              >
                {shown.length === 0 ? (
                  <p role="status" className="px-3 py-5 text-center text-[12.5px] text-mute">Nothing matches.</p>
                ) : (
                  grouped.map((section, gi) => (
                    <div key={gi}>
                      {section.group ? (
                        <div role="presentation" className="px-2.5 pt-2 pb-1 text-[10px] font-bold tracking-[.08em] text-ghost uppercase">
                          {section.group}
                        </div>
                      ) : null}
                      {section.items.map((option) => {
                        const index = shown.indexOf(option);
                        const active = index === cursor;
                        const selected = option.value === value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            id={listId + '-' + index}
                            role="option"
                            aria-selected={selected}
                            tabIndex={-1}
                            data-active={active ? '1' : '0'}
                            onMouseMove={() => setCursor(index)}
                            onClick={() => pick(option)}
                            className={cn(
                              'flex w-full items-center gap-2.5 rounded-[calc(8px*var(--radius-mul,1))] px-2.5 py-1.5 text-left transition-colors',
                              active ? 'bg-surface-4' : 'hover:bg-surface-4'
                            )}
                          >
                            {option.art ? (
                              <img
                                src={option.art}
                                alt=""
                                loading="lazy"
                                className="h-[22px] w-[22px] flex-none rounded-[calc(6px*var(--radius-mul,1))] object-cover"
                              />
                            ) : option.icon ? (
                              <Icon
                                name={option.icon}
                                size={17}
                                className="flex-none"
                                style={{ color: option.color }}
                              />
                            ) : option.color ? (
                              <span
                                className="h-3 w-3 flex-none rounded-full"
                                style={{ background: option.color }}
                              />
                            ) : null}
                            <span className="min-w-0 flex-1">
                              <span className="block truncate text-[13px] font-semibold">{option.label}</span>
                              {option.sub ? (
                                <span className="block truncate text-[11.5px] text-mute">{option.sub}</span>
                              ) : null}
                            </span>
                            {selected ? <Icon name="check" size={15} className="flex-none text-accent-hi" /> : null}
                          </button>
                        );
                      })}
                    </div>
                  ))
                )}
              </div>
              <style>{`
                @keyframes selectdown { from { opacity:0; transform: scaleY(.92) translateY(-6px) } to { opacity:1; transform:none } }
                @keyframes selectup { from { opacity:0; transform: scaleY(.92) translateY(6px) } to { opacity:1; transform:none } }
              `}</style>
            </div>,
            document.body
          )
        : null}
    </>
  );
}
